import React, { useMemo, useState } from "react";
import moment from "moment";
import { Tabs, Badge, Dropdown, Button, Input, Space, Select, Avatar } from "antd";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import PredefinedDateRanges from "../../../../../core/common/datePicker";

import FacebookMenu from "../leads-in-queue/menus/FacebookMenu";
import WhatsAppMenu from "../leads-in-queue/menus/WhatsAppMenu";
import PublicFormMenu from "../leads-in-queue/menus/PublicFormMenu";

import type {
    ActiveCompany,
    DateRange,
    Employee,
    SourceFilter,
    SourceHierarchy,
} from "../../../../../core/data/redux/leads-queue/types";

type SourceTypeCounts = {
    all: number;
    facebook: number;
    whatsapp: number;
    public_form: number;
    crm: number;
    unknown: number;
};

interface LeadsHistoryFiltersProps {
    activeCompany: ActiveCompany;
    employees: Employee[];
    hasFormScope: boolean;

    activeSourceType: string;
    sourceTypeCounts: SourceTypeCounts | null | any;

    sourceHierarchy: SourceHierarchy | null;
    sourceFilter: SourceFilter;
    dateRange: DateRange;

    isLoading: boolean;
    displayTotalCount: number;

    onSourceTypeChange: (key: string) => void | Promise<void>;
    onSourceFilterChange: (filter: SourceFilter) => void | Promise<void>;
    onDateRangeChange: (range: DateRange) => void | Promise<void>;
    onReset: () => void | Promise<void>;
    onSearch: (term: string) => void | Promise<void>;
    activeAssignedTo: string | null;
    onAssignedToChange: (userId: string | null) => void | Promise<void>;
}

const LeadsHistoryFilters: React.FC<LeadsHistoryFiltersProps> = ({
    activeCompany,
    employees,
    hasFormScope,
    activeSourceType,
    sourceTypeCounts,
    sourceHierarchy,
    sourceFilter,
    dateRange,
    isLoading,
    displayTotalCount,
    onSourceTypeChange,
    onSourceFilterChange,
    onDateRangeChange,
    onReset,
    onSearch,
    activeAssignedTo,
    onAssignedToChange,
}) => {
    const [localSearch, setLocalSearch] = useState("");

    const safeCounts: SourceTypeCounts = {
        all: sourceTypeCounts?.total ?? 0,
        facebook: sourceTypeCounts?.facebook ?? 0,
        whatsapp: sourceTypeCounts?.whatsapp ?? 0,
        public_form: sourceTypeCounts?.public_form ?? 0,
        crm: sourceTypeCounts?.crm ?? 0,
        unknown: sourceTypeCounts?.unknown ?? 0,
    };

    const handleSearchClick = () => {
        onSearch(localSearch);
    };

    const handleResetClick = () => {
        setLocalSearch("");
        onReset();
    };

    const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
        const newDateRange: DateRange = {
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
        };
        onDateRangeChange(newDateRange);
    };

    const renderFilterDropdown = () => {
        if (activeSourceType === "all" || activeSourceType === "crm" || activeSourceType === "unknown") {
            return null;
        }

        let menu: React.ReactNode = null;
        let buttonText = "";

        switch (activeSourceType) {
            case "facebook":
                menu = (
                    <FacebookMenu
                        sourceHierarchy={sourceHierarchy}
                        activeFilter={sourceFilter}
                        onChange={onSourceFilterChange}
                    />
                );
                buttonText = "Filter by Facebook";
                break;

            case "whatsapp":
                menu = (
                    <WhatsAppMenu
                        sourceHierarchy={sourceHierarchy}
                        activeFilter={sourceFilter}
                        onChange={onSourceFilterChange}
                    />
                );
                buttonText = "Filter by WhatsApp";
                break;

            case "public_form":
                menu = (
                    <PublicFormMenu
                        sourceHierarchy={sourceHierarchy}
                        activeFilter={sourceFilter}
                        onChange={onSourceFilterChange}
                    />
                );
                buttonText = "Filter by Public Form";
                break;
        }

        if (!menu) return null;

        return (
            <Dropdown dropdownRender={() => menu as any} trigger={["click"]}>
                <Button className="d-inline-flex align-items-center">
                    {buttonText} <DownOutlined className="ms-2" />
                </Button>
            </Dropdown>
        );
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (activeSourceType !== "all") count++;
        if (sourceFilter.level_1 || sourceFilter.level_2 || sourceFilter.level_3) count++;
        if (dateRange.start_date && dateRange.end_date) count++;
        return count;
    };

    const activeFiltersCount = getActiveFiltersCount();

    const sourceTypeTabs = [
        {
            key: "all",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-list-check me-2" />
                    <span className="fw-medium">All</span>
                    <Badge
                        count={safeCounts.all}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#1890ff" }}
                    />
                </span>
            ),
        },
        {
            key: "facebook",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-brand-facebook me-2" style={{ color: "#1877F2" }} />
                    <span className="fw-medium">Facebook</span>
                    <Badge
                        count={safeCounts.facebook}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#1877F2" }}
                    />
                </span>
            ),
        },
        {
            key: "whatsapp",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-brand-whatsapp me-2" style={{ color: "#25D366" }} />
                    <span className="fw-medium">WhatsApp</span>
                    <Badge
                        count={safeCounts.whatsapp}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#25D366" }}
                    />
                </span>
            ),
        },
        {
            key: "public_form",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-forms me-2" style={{ color: "#0066FF" }} />
                    <span className="fw-medium">Public Forms</span>
                    <Badge
                        count={safeCounts.public_form}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#0066FF" }}
                    />
                </span>
            ),
        },
        {
            key: "crm",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-building me-2" style={{ color: "#FF6B35" }} />
                    <span className="fw-medium">CRM</span>
                    <Badge
                        count={safeCounts.crm}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#FF6B35" }}
                    />
                </span>
            ),
        },
        {
            key: "unknown",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-question-mark me-2" style={{ color: "#6C757D" }} />
                    <span className="fw-medium">Other</span>
                    <Badge
                        count={safeCounts.unknown}
                        className="ms-2"
                        showZero
                        style={{ fontSize: 10, backgroundColor: "#6C757D" }}
                    />
                </span>
            ),
        },
    ];

    const employeeOptions = useMemo(() => {
        return employees?.map((user: any) => ({
            label: user.full_name || `${user.fname} ${user.lname}`,
            value: user.id.toString(),
            key: user.id,
            image_url: user?.image_url || "assets/img/users/user.png"
        })) || [];
    }, [employees]);

    return (
        <div className="card">
            <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h4 className="mb-1 d-flex align-items-center">
                            <i className="ti ti-history text-primary fs-32 me-2" />
                            History
                        </h4>
                        <p className="text-muted mb-0">View assigned leads history from all sources</p>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {isLoading ? (
                            <span className="badge badge-lg badge-info-transparent">
                                <i className="ti ti-loader-2 me-1 rotating" />
                                Loading...
                            </span>
                        ) : (
                            <span className="badge badge-lg badge-info-transparent">
                                <i className="ti ti-database me-1" />
                                Total: {displayTotalCount}
                            </span>
                        )}
                    </div>
                </div>

                <Tabs
                    activeKey={activeSourceType}
                    onChange={onSourceTypeChange}
                    type="line"
                    size="small"
                    className="modern-tabs"
                    items={sourceTypeTabs}
                />
            </div>

            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div className="d-flex gap-2">
                    <div className="d-flex gap-2 mb-2 me-2">
                        <Input
                            placeholder="Search leads..."
                            value={localSearch}
                            onChange={(e) => {
                                const value = e.target.value;
                                setLocalSearch(value);
                                if (value === "") {
                                    onSearch("");
                                }
                            }}
                            onPressEnter={handleSearchClick}
                            style={{ width: 230 }}
                            allowClear
                        />
                        <Button type="primary" onClick={handleSearchClick}>
                            <i className="ti ti-search me-1" />
                        </Button>
                    </div>
                    {renderFilterDropdown()}
                </div>

                <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                    <div className="me-2 mb-2">
                        <Space>
                            <Select
                                style={{ width: 200 }}
                                placeholder="Filter by Employee"
                                value={activeAssignedTo || undefined}
                                onChange={(value) => onAssignedToChange(value?.toString() ?? null)}
                                allowClear
                                showSearch
                                options={employeeOptions}
                                filterOption={(input, option: any) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                optionRender={(option) => (
                                    <Space>
                                        {option?.data?.image_url && (
                                            <Avatar
                                                size="small"
                                                src={option?.data?.image_url}
                                                icon={<UserOutlined />}
                                            />
                                        )}
                                        <span>{option.label}</span>
                                    </Space>
                                )}
                            />
                        </Space>
                    </div>
                    <div className="me-3">
                        <PredefinedDateRanges
                            onDateChange={handleDateChange}
                            startDate={dateRange.start_date ? moment(dateRange.start_date) : null}
                            endDate={dateRange.end_date ? moment(dateRange.end_date) : null}
                        />
                    </div>

                    <div>
                        <button
                            type="button"
                            className={`btn ${activeFiltersCount > 0 ? "btn-warning" : "btn-success"
                                } mb-2 position-relative`}
                            onClick={handleResetClick}
                            disabled={!activeCompany.company_id || !hasFormScope}
                        >
                            <i className="ti ti-refresh me-1" />
                            Reset
                            {activeFiltersCount > 0 && (
                                <span
                                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                    style={{ fontSize: "10px" }}
                                >
                                    {activeFiltersCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadsHistoryFilters;
